import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Calendar, Send, BarChart3 } from 'lucide-react';
import { V1Layout } from '../../components/v1/V1Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../ui/card';
import { Button } from '../../../../ui/button';
import { epicengageSupabase } from '../../../../../integrations/supabase/client';
import { useMerchant } from '../../../../../hooks/useMerchant';

export default function Overview() {
  const { merchant, loading } = useMerchant();
  const [stats, setStats] = useState({
    automationsEnabled: 0,
    automationsTotal: 3,
    eventsEnabled: 0,
    sentToday: 0
  });

  useEffect(() => {
    if (!merchant) return;

    const fetchStats = async () => {
      // Count enabled sequences
      const { count: enabledCount } = await epicengageSupabase
        .from('sequences')
        .select('*', { count: 'exact', head: true })
        .eq('merchant_id', merchant.id)
        .eq('enabled', true);

      // Count enabled events
      const { count: eventsCount } = await epicengageSupabase
        .from('enabled_events')
        .select('*', { count: 'exact', head: true })
        .eq('merchant_id', merchant.id)
        .eq('enabled', true);

      // Count emails SENT today (not scheduled)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const { count: sentCount } = await epicengageSupabase
        .from('scheduled_messages')
        .select('*', { count: 'exact', head: true })
        .eq('merchant_id', merchant.id)
        .eq('status', 'SENT')
        .gte('sent_at', today.toISOString())
        .lt('sent_at', tomorrow.toISOString());

      setStats({
        automationsEnabled: enabledCount || 0,
        automationsTotal: 3,
        eventsEnabled: eventsCount || 0,
        sentToday: sentCount || 0
      });
    };

    fetchStats();
  }, [merchant]);

  if (!merchant) {
    return (
      <V1Layout>
        <div className="p-8 flex items-center justify-center">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </V1Layout>
    );
  }

  return (
    <V1Layout>
      <div>
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Welcome back!</h1>
          <p className="text-muted-foreground mt-1">
            Here's what's happening with your email campaigns
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Automations</CardTitle>
              <Zap className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.automationsEnabled}/{stats.automationsTotal}
              </div>
              <p className="text-xs text-muted-foreground">
                sequences enabled
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Events Enabled</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.eventsEnabled}</div>
              <p className="text-xs text-muted-foreground">
                holiday campaigns active
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Sent Today</CardTitle>
              <Send className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.sentToday}</div>
              <p className="text-xs text-muted-foreground">
                emails sent
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" />
                Send Manual Blast
              </CardTitle>
              <CardDescription>
                Send a one-time email to your customers now
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild className="w-full">
                <Link to="/clientpanel/enage/blast">Send Now</Link>
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                View Analytics
              </CardTitle>
              <CardDescription>
                See performance metrics for sent emails
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild variant="outline" className="w-full">
                <Link to="/clientpanel/enage/analytics">View Analytics</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </V1Layout>
  );
}
